import { browserName, isMobile, osName } from 'react-device-detect';
import {
  EPublisherWebhookEventType,
  ELoginMethods,
  ELoginEvents,
  ELocalStorageKeys,
  ESessionStorageKeys,
  ELoginEventsV2,
  EPublisherWebhookEventTypeV2,
} from '../constants/enums';
import useApi from './useApi';
import useCustomEvents from './useCustomEvents';
import { appStateService } from 'state/app.state.service';
import { webStorageUtil } from 'state/webStorage.state.service';
import { LoginEventData } from 'pages/login/types';

export default function useLoginEvents() {
  const customEvents = useCustomEvents();
  const API = useApi({
    platform: isMobile ? osName : browserName,
  });

  const sendPublisherWebhookLoginEvent = async ({ //TODO: remove after events v1 deprecation
    eventName,
    method,
    options,
  }: {
    eventName: ELoginEvents;
    method?: ELoginMethods;
    options?: Record<string, any>;
  }) => {
    try {
      const utmParams = appStateService.utms.get() || {};
      const sharedProps = customEvents.getEventsSharedProps();
      const data: LoginEventData = {
        eventName,
        method,
        source: webStorageUtil.get(ESessionStorageKeys.SOURCE) || 'browser app',
        utmSource: utmParams.utm_source,
        utmCampaign: utmParams.utm_campaign,
        utmMedium: utmParams.utm_medium,
        utmContent: utmParams.utm_content,
        utmTerm: utmParams.utm_term,
        session_id: sharedProps.sessionId,
        publisher_id: sharedProps.publisherId,
        store_version: sharedProps.storeVersion,
        shared_ui_version: sharedProps.sharedUiVersion,
        utm_source: utmParams.utm_source ?? null,
        utm_campaign: utmParams.utm_campaign ?? null,
        utm_medium: utmParams.utm_medium ?? null,
        utm_term: utmParams.utm_term ?? null,
        utm_content: utmParams.utm_content ?? null,
        ...options,
        ...sharedProps,
      };
      API.sendPublisherWebhookEvent.mutate({
        eventType: EPublisherWebhookEventType.LOGIN,
        data,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const sendPublisherWebhookLoginEventV2 = async ({
    eventName,
    eventType,
    method,
    options,
  }: {
    eventName: ELoginEventsV2;
    eventType: EPublisherWebhookEventTypeV2.POST_LOGIN | EPublisherWebhookEventTypeV2.PRE_LOGIN;
    method?: ELoginMethods;
    options?: Record<string, any>;
  }) => {
    try {
      const utmParams = appStateService.utms.get() || {};
      const sharedProps = customEvents.getEventsSharedProps();
      const data: LoginEventData = {
        eventName,
        method,
        source: webStorageUtil.get(ESessionStorageKeys.SOURCE) || 'browser app',
        utmSource: utmParams.utm_source,
        utmCampaign: utmParams.utm_campaign,
        utmMedium: utmParams.utm_medium,
        utmContent: utmParams.utm_content,
        utmTerm: utmParams.utm_term,
        session_id: sharedProps.sessionId,
        publisher_id: sharedProps.publisherId,
        store_version: sharedProps.storeVersion,
        shared_ui_version: sharedProps.sharedUiVersion,
        utm_source: utmParams.utm_source ?? null,
        utm_campaign: utmParams.utm_campaign ?? null,
        utm_medium: utmParams.utm_medium ?? null,
        utm_term: utmParams.utm_term ?? null,
        utm_content: utmParams.utm_content ?? null,
        ...options,
        ...sharedProps,
      };
      API.sendPublisherWebhookEventV2.mutate({
        eventType,
        data,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const recordLoginStartTime = (): void => {
    webStorageUtil.set(ELocalStorageKeys.LOGIN_START_TIME, new Date().getTime());
  };

  const calcLoginDuration = (): number => {
    const loginEndTime = new Date().getTime();
    const loginStartTime = webStorageUtil.get(ELocalStorageKeys.LOGIN_START_TIME);
    return Math.round((loginEndTime - loginStartTime) / 1000);
  };

  const calcLoginDurationMs = (): number => {
    const loginEndTime = new Date().getTime();
    const loginStartTime = webStorageUtil.get(ELocalStorageKeys.LOGIN_START_TIME);
    return loginStartTime ? loginEndTime - loginStartTime : 0;
  };

  return {
    sendPublisherWebhookLoginEvent,
    sendPublisherWebhookLoginEventV2,
    recordLoginStartTime,
    calcLoginDuration,
    calcLoginDurationMs,
  };
}
